import React, { useState, useRef, useEffect } from 'react';
import { t } from '../i18n';
import { fmtDay, fmtMoney } from '../lib/format';

// The client-facing PROJECT STATEMENT — one row per trade, each with its Joist
// invoice link, what was billed, what's been paid and what's still owed. This
// is the view Julian fills in with the homeowner sitting next to him, so it
// deliberately shows nothing about cost or profit.
// `onPay` is the customer's way to pay online, in their own portal. `onCopyPayLink`
// is Julian's way to send them the link. Either is left off where it does not
// belong, and the button goes with it.
export default function ProjectStatement({
  project, onRecordPayment, onEditLine, onDeleteLine, onSendLine, onOpenInvoice, isAdmin, onPay, onCopyPayLink,
  onEditPayment, onDeletePayment,
  // ── TYPING INTO THE STATEMENT ─────────────────────────────────────────
  //
  // Julian reads this the way he read his spreadsheet, and asked to work it
  // the same way: click a cell and it lights up so he can see where he is,
  // click it again and type. Passed in rather than done here, because
  // saving is a request and this component has never made one.
  //
  //   onSaveCell({ kind: 'line'|'payment', id, field, value })  → Promise
  //
  // Omitted and the grid is read-only, which is what the homeowner's copy
  // and the client page both want.
  onSaveCell,
}) {
  // Which cell is lit, and which one is being typed into. Row is the index
  // in the order ON SCREEN, so it still points at the same cell when a line
  // turns green and drops below the rule.
  const [sel, setSel] = useState(null);        // { r, c }
  const [editing, setEditing] = useState(null); // { r, c }
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  if (!project) return null;
  const lines = project.lines || [];
  const lineName = (id) => (lines.find(l => String(l._id) === String(id)) || {}).trade || '';

  // Still owing floats to the top. Everything else keeps invoice order, so a
  // line turning green drops back where it belongs without anybody sorting.
  const owing = (l) => (Number(l.owed) || 0) > 0.004;
  const ordered = [...lines.filter(owing), ...lines.filter(l => !owing(l))];
  const owingCount = lines.filter(owing).length;

  // The cheques against each line, in the order they were taken, which is
  // how the spreadsheet reads them left to right.
  const byLine = {};
  (project.payments || []).forEach((p) => {
    const k = String(p.lineId);
    (byLine[k] = byLine[k] || []).push(p);
  });
  const paysFor = (id) => byLine[String(id)] || [];
  // As many Payment groups as the busiest line needs, and no more. Three on
  // the Skanchy basement; it grows on its own if a job ever needs four.
  const payCols = Array.from(
    { length: Math.max(0, ...lines.map(l => paysFor(l._id).length)) },
    (_, i) => i,
  );
  const colTotal = (i) => lines.reduce((t, l) => t + (Number(paysFor(l._id)[i]?.amount) || 0), 0);
  // An empty column of buttons is still a column. It used to appear on the
  // homeowner's copy, and on the client page, because isAdmin alone turned
  // it on.
  const acts = Boolean(
    (isAdmin && (onRecordPayment || onEditLine || onDeleteLine || onSendLine)) || onPay,
  );

  // ── THE COLUMNS ───────────────────────────────────────────────────────
  //
  // One entry per column, in the order they are drawn, each saying how to
  // show itself and whether it can be typed into. Written out like this
  // rather than inline in the JSX because the keyboard has to be able to
  // walk them, and a grid you can only read with your eyes is not a grid.
  //
  // Total paid, Owed and Status are deliberately NOT editable. They are
  // added up from the rows beside them, and a figure you can type over is
  // a figure that stops agreeing with the payments that made it.
  const canType = Boolean(isAdmin && onSaveCell);
  const dateInput = (d) => {
    if (!d) return '';
    const x = new Date(d);
    if (isNaN(x)) return '';
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };
  // Typed as a bare day, stored at midday, so a date never reads as the one
  // before it in Utah.
  const fromDateInput = (v) => {
    const [y, m, d] = String(v || '').split('-').map(Number);
    return y ? new Date(y, m - 1, d, 12).toISOString() : null;
  };
  const fromMoney = (v) => {
    const n = Number(String(v == null ? '' : v).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
  };
  // A card payment is what the bank did, not what anybody typed, so it is
  // read-only here exactly as it is in the list underneath.
  const byHand = (p) => p && !p.stripePaymentIntentId && !p.stripeSessionId;

  const lineCell = (cls, field, type, show, get) => ({
    cls, type, show,
    at: (l) => (canType ? { kind: 'line', id: l._id, field, value: get(l) } : null),
  });
  const readCell = (cls, show, why) => ({ cls, show, why, at: () => null });
  const payCell = (n, cls, field, type, show, get) => ({
    cls, type, show: (l) => show(paysFor(l._id)[n]),
    at: (l) => {
      const p = paysFor(l._id)[n];
      return canType && byHand(p) ? { kind: 'payment', id: p._id, field, value: get(p) } : null;
    },
    why: (l) => {
      const p = paysFor(l._id)[n];
      if (!p) return 'No payment here yet. Record one with the button at the end of the row.';
      if (!byHand(p)) return 'Paid by card. This is what the bank did, so it cannot be typed over.';
      return '';
    },
  });

  // ── OPENING THE BILL ──
  //
  // Julian: click the name or the number and bring up the invoice. Both the
  // customer and the admin, both reading the same statement, both wanting
  // the same thing — what exactly is this money for.
  //
  // It is the TEXT that is the link, not the cell, so the admin's grid is
  // untouched: a press on the rest of the cell still selects it and a second
  // press still types into it. stopPropagation keeps the two apart.
  const openable = (l, text) => (onOpenInvoice && text
    ? (
      <button
        type="button" className="st-link"
        title="Open this invoice"
        onClick={(e) => { e.stopPropagation(); onOpenInvoice(l); }}
      >{text}</button>
    )
    : text);

  const COLS = [
    lineCell('g1 dim', 'invoiceDate', 'date',
      (l) => (l.invoiceDate ? fmtDay(l.invoiceDate) : ''), (l) => dateInput(l.invoiceDate)),
    { ...lineCell('g1 st-desc', 'trade', 'text', (l) => l.trade, (l) => l.trade || ''),
      show: (l) => openable(l, l.trade) },
    { ...lineCell('g2', 'invoiceNumber', 'text', (l) => l.invoiceNumber, (l) => l.invoiceNumber || ''),
      // An invoiceUrl points at the bill in Joist, which is a DIFFERENT
      // document from the one this app holds. Where both exist the app's own
      // sheet wins: it is the one with the live balance on it.
      show: (l) => (onOpenInvoice
        ? openable(l, l.invoiceNumber)
        : l.invoiceUrl
          ? <button type="button" className="st-link" onClick={(e) => { e.stopPropagation(); window.open(l.invoiceUrl, '_blank'); }}>{l.invoiceNumber}</button>
          : l.invoiceNumber) },
    lineCell('g2 n', 'amount', 'money', (l) => fmtMoney(l.amount), (l) => String(l.amount ?? '')),
    readCell('g3 n', (l) => fmtMoney(l.paid), 'Added up from the payments on this line.'),
    readCell('g4 n bal', (l) => fmtMoney(l.owed), 'What was billed, less what has come in.'),
    readCell('g4 st-status', (l) => (owing(l) ? t('pj.dueBalance') : t('pj.fullyPaid')),
      'Set by the figures beside it. Record a payment and it turns green on its own.'),
    ...payCols.flatMap((n) => [
      payCell(n, `p${n % 3} dim sm`, 'date', 'date',
        (p) => (p ? fmtDay(p.date) : ''), (p) => dateInput(p.date)),
      payCell(n, `p${n % 3} n sm`, 'amount', 'money',
        (p) => (p ? fmtMoney(p.amount) : ''), (p) => String(p.amount ?? '')),
      payCell(n, `p${n % 3} sm`, 'method', 'text',
        (p) => (p ? [p.method, p.reference && `#${p.reference}`].filter(Boolean).join(' ') : ''),
        (p) => p.method || ''),
    ]),
  ];

  // ── Working it ────────────────────────────────────────────────────────
  const targetAt = (r, c) => (ordered[r] && COLS[c] ? COLS[c].at(ordered[r]) : null);

  const openCell = (r, c) => {
    const same = sel && sel.r === r && sel.c === c;
    if (editing) return;                       // a click elsewhere commits first
    if (!same) { setSel({ r, c }); return; }   // one tap lights it up
    const t2 = targetAt(r, c);
    if (!t2) return;                           // computed, or nothing there
    setDraft(t2.value);
    setEditing({ r, c });
  };

  const commit = async () => {
    const at = editing;
    if (!at) return;
    const t2 = targetAt(at.r, at.c);
    setEditing(null);
    if (!t2) return;
    const col = COLS[at.c];
    let value = draft;
    if (col.type === 'money') {
      value = fromMoney(draft);
      if (value === null) { alert(`"${draft}" is not an amount.`); return; }
    } else if (col.type === 'date') {
      value = fromDateInput(draft);
      if (!value) { alert('Pick a date.'); return; }
    } else {
      value = String(draft).trim();
    }
    if (String(value) === String(t2.value)) return;   // nothing moved
    setSaving(true);
    try {
      await onSaveCell({ kind: t2.kind, id: t2.id, field: t2.field, value });
    } finally { setSaving(false); }
  };

  const onKey = (e) => {
    if (!sel) return;
    if (editing) {
      if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
      return;                                   // the input handles the rest
    }
    const move = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[e.key];
    if (move) {
      e.preventDefault();
      setSel({
        r: Math.min(ordered.length - 1, Math.max(0, sel.r + move[0])),
        c: Math.min(COLS.length - 1, Math.max(0, sel.c + move[1])),
      });
      return;
    }
    if (e.key === 'Enter') { e.preventDefault(); openCell(sel.r, sel.c); }
    if (e.key === 'Escape') setSel(null);
  };

  const Cell = ({ label, value, bg, color }) => (
    <div style={{ flex: 1, minWidth: 110, background: bg, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
      <div style={{ fontSize: 19, color }}>{fmtMoney(value)}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Cell label={t('pj.invoiced')} value={project.invoicedTotal} bg="#f1f5f9" color="var(--slate)" />
        <Cell label={t('pj.received')} value={project.receivedTotal} bg="var(--green-light)" color="var(--green)" />
        <Cell label={t('pj.owed')} value={project.owedByClient} bg="var(--red-light)" color="var(--red)" />
      </div>

      {!isAdmin && onPay && project.owedByClient > 0.004 && (
        <button className="btn btn-primary" style={{ width: '100%', marginBottom: 14 }} onClick={() => onPay(project)}>
          Pay {fmtMoney(project.owedByClient)} online
        </button>
      )}
      {isAdmin && onCopyPayLink && project.owedByClient > 0.004 && (
        <button className="btn btn-outline btn-sm" style={{ marginBottom: 14 }} onClick={() => onCopyPayLink(project)}>
          Copy pay link for the customer
        </button>
      )}

      {project.credit > 0 && (
        <div className="alert alert-green">{t('pj.credit')} {fmtMoney(project.credit)}</div>
      )}

      {/* ── THE STATEMENT ───────────────────────────────────────────────
          One row per trade, read the way Julian reads his spreadsheet: a
          line, what it was billed, what has come in, what is left, and the
          cheques that paid it running off to the right.

          Two rules it keeps on its own, which is the point of it:

            · anything still owing floats above the black rule, in red. When
              the last of it is paid the row turns green and drops back into
              invoice order by itself. Nobody sorts anything.
            · the totals are added from the rows every time. His own sheet's
              three SUM ranges stopped short as rows were added underneath
              them, understating six months of invoicing by $2,026.

          A table, not a grid of divs: a trade called WASHING MACHINE TRAP IN
          THE SECOND FLOOR makes its own row wider, and only a table shares
          one set of column widths across all of them. */}
      {lines.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>{t('pj.noLines')}</div>
      ) : (
        <div
          className={`st-wrap${canType ? ' st-live' : ''}`}
          ref={gridRef}
          tabIndex={canType ? 0 : undefined}
          onKeyDown={canType ? onKey : undefined}
        >
          <table className="st">
            <thead>
              <tr className="st-groups">
                <th colSpan={2} className="g1">Invoice</th>
                <th colSpan={2} className="g2">Billed</th>
                <th className="g3">Received</th>
                <th colSpan={2} className="g4">Outstanding</th>
                {payCols.map(i => <th key={i} colSpan={3} className={`p${i % 3}`}>Payment {i + 1}</th>)}
                {acts && <th className="g4" />}
              </tr>
              <tr className="st-heads">
                <th className="g1">Date</th>
                <th className="g1">Description</th>
                <th className="g2">Inv #</th>
                <th className="g2 n">Amount</th>
                <th className="g3 n">Total paid</th>
                <th className="g4 n">Owed</th>
                <th className="g4">Status</th>
                {payCols.map(i => (
                  <React.Fragment key={i}>
                    <th className={`p${i % 3}`}>Date</th>
                    <th className={`p${i % 3} n`}>Amount</th>
                    <th className={`p${i % 3}`}>Form</th>
                  </React.Fragment>
                ))}
                {acts && <th className="g4" />}
              </tr>
            </thead>
            <tbody>
              {ordered.map((l, r) => {
                const due = owing(l);
                return (
                  <tr key={l._id} className={`${due ? 'due' : 'ok'}${r === owingCount - 1 ? ' lastdue' : ''}`}>
                    {COLS.map((col, c) => {
                      const lit = sel && sel.r === r && sel.c === c;
                      const typing = editing && editing.r === r && editing.c === c;
                      const at = col.at(l);
                      const why = typeof col.why === 'function' ? col.why(l) : col.why;
                      const body = col.show(l);
                      return (
                        <td
                          key={c}
                          className={`${col.cls}${lit ? ' sel' : ''}${canType && !at ? ' ro' : ''}`}
                          onClick={() => openCell(r, c)}
                          // The whole description, without making the row
                          // three lines deep to hold it.
                          title={typing ? undefined : (lit && why) || (typeof body === 'string' ? body : undefined)}
                        >
                          {typing ? (
                            <input
                              ref={inputRef}
                              className="st-in"
                              type={col.type === 'date' ? 'date' : 'text'}
                              inputMode={col.type === 'money' ? 'decimal' : undefined}
                              value={draft}
                              disabled={saving}
                              onChange={(e) => setDraft(e.target.value)}
                              onBlur={commit}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                                if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                              }}
                            />
                          ) : body}
                        </td>
                      );
                    })}
                    {acts && (
                      <td className="g4 st-acts">
                        {!isAdmin && onPay && due && (
                          <button className="btn btn-green btn-sm" onClick={() => onPay(project, l)}>Pay {fmtMoney(l.owed)}</button>
                        )}
                        {isAdmin && onRecordPayment && due && (
                          <button className="btn btn-green btn-sm" onClick={() => onRecordPayment(l)}>Record</button>
                        )}
                        {/* ── Emailing this one bill ──
                            Raising an invoice does not email anybody any
                            more, which is right for a statement catching up
                            on work already agreed and wrong for the bill a
                            customer is actually waiting on. This is that
                            send, and it is per LINE because the plumbing
                            invoice is what they are waiting for, not the
                            project.

                            Only on a line with a balance: a Send button on a
                            paid bill is an invitation to chase somebody who
                            has already paid. Once it has gone, the button
                            says so and when. */}
                        {isAdmin && onSendLine && due && (
                          <button
                            className={`btn btn-sm ${l.sentAt ? 'btn-ghost' : 'btn-outline'}`}
                            title={l.sentAt ? `Emailed ${fmtDay(l.sentAt)}${l.sentTo ? ` to ${l.sentTo}` : ''}` : 'Email this invoice to the customer'}
                            onClick={() => onSendLine(l)}
                          >
                            {l.sentAt ? `Sent ${fmtDay(l.sentAt)}` : 'Send'}
                          </button>
                        )}
                        {isAdmin && onEditLine && <button className="btn btn-ghost btn-sm" onClick={() => onEditLine(l)}>Edit</button>}
                        {isAdmin && onDeleteLine && (
                          <button className="btn btn-ghost btn-sm btn-icon" aria-label="Delete line"
                            onClick={() => onDeleteLine(l)}>&times;</button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="g1" />
                <td className="g1">{lines.length} {lines.length === 1 ? 'line' : 'lines'}</td>
                <td className="g2" />
                <td className="g2 n">{fmtMoney(project.invoicedTotal)}</td>
                <td className="g3 n">{fmtMoney(project.receivedTotal)}</td>
                <td className="g4 n bal">{fmtMoney(project.owedByClient)}</td>
                <td className="g4" />
                {payCols.map(i => (
                  <React.Fragment key={i}>
                    <td className={`p${i % 3}`} />
                    <td className={`p${i % 3} n`}>{fmtMoney(colTotal(i))}</td>
                    <td className={`p${i % 3}`} />
                  </React.Fragment>
                ))}
                {acts && <td className="g4" />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Payments received, the way the spreadsheet lists them across the top */}
      {project.payments?.length > 0 && (
        <>
          <div className="sec-label" style={{ marginTop: 16 }}>{t('pj.paymentsReceived')}</div>
          {/* A real table, not a row of spans. Columns in separate flex or
              grid rows only LOOK aligned until one trade is called WASHING
              MACHINE TRAP IN THE SECOND FLOOR; a table shares its column
              widths across every row by definition, which is the whole
              property wanted here. */}
          <div className="lead-card pay-list">
            <table className="pay-table">
              <tbody>
                {project.payments.map(p => (
                  <tr key={p._id} className={p.status === 'failed' ? 'failed' : ''}>
                    <td className="dim pay-date">{fmtDay(p.date)}</td>
                    <td className="pay-method"><span className="tag tag-slate">{p.method}</span></td>
                    {/* Which trade it paid. Two cheques for $1,237.50 on the
                        same day is a real thing on this statement, and
                        without the trade there is no telling them apart
                        before pressing Delete on one. */}
                    <td className="pay-trade">
                      {lineName(p.lineId) || <span className="dim">Unassigned</span>}
                      {/* A bank payment takes days to clear and does not
                          count toward what is paid until it does. */}
                      {p.status === 'pending' && <span className="tag tag-amber">Processing</span>}
                      {p.status === 'failed' && <span className="tag tag-red">Failed</span>}
                    </td>
                    <td className="dim pay-ref">{p.reference ? `#${p.reference}` : ''}</td>
                    <td className="pay-amt" style={{
                      color: p.status === 'pending' ? 'var(--amber)' : p.status === 'failed' ? 'var(--muted)' : 'var(--green)',
                    }}>{fmtMoney(p.amount)}</td>
                    <td className="pay-acts">
                      {/* A Stripe payment has neither button: that record is
                          what the bank did, not what we typed. */}
                      {isAdmin && !p.stripePaymentIntentId && !p.stripeSessionId && (
                        <>
                          {onEditPayment && (
                            <button className="btn btn-ghost btn-sm" onClick={() => onEditPayment(p)}>Edit</button>
                          )}
                          {onDeletePayment && (
                            <button className="btn btn-ghost btn-sm btn-icon" aria-label="Delete payment"
                              onClick={() => onDeletePayment(p)}>&times;</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
